import React, { Dispatch, PropsWithChildren, useEffect, useState } from 'react';
import { createContext, FunctionComponent } from 'react';
import { fetchFloors, Floors } from '../Api/FloorsApi';
import { AccessoriesData, fetchAccessories } from '../Api/AccessoriesApi';
import { FloorFilter } from '../Pages/FloorPage/FloorPage';
import { persistAnswers, readAnswers } from '../Api/PersistenceApi';
import { InsulationFilter } from '../Pages/Accessories/InsultationPage/InsulationPage';

declare global {
    interface Window {
        __GGLocale: string;
    }
}

interface ContextType {
    roomLength: string;
    setRoomLength: Dispatch<React.SetStateAction<string>>;
    roomWidth: string;
    setRoomWidth: Dispatch<React.SetStateAction<string>>;
    roomSqm: string;
    setRoomSqm: Dispatch<React.SetStateAction<string>>;
    wallLength: string;
    setWallLength: Dispatch<React.SetStateAction<string>>;
    innerCorners: string;
    setInnerCorners: Dispatch<React.SetStateAction<string>>;
    corners: string;
    setCorners: Dispatch<React.SetStateAction<string>>;
    endCaps: string;
    setEndCaps: Dispatch<React.SetStateAction<string>>;
    floors: Floors;
    selectedDecor: string;
    setSelectedDecor: (articleNumber: string) => void;
    accessories: AccessoriesData;
    selectedInsulation: string;
    setSelectedInsulation: Dispatch<React.SetStateAction<string>>;
    selectedSkirtingDecor: string;
    setSelectedSkirtingDecor: Dispatch<React.SetStateAction<string>>;
    selectedInstallationAndCare: string[];
    setSelectedInstallationAndCare: Dispatch<React.SetStateAction<string[]>>;
    sendUserInputToServer: () => void;
    slug: string | null;
    setSlug: Dispatch<React.SetStateAction<string | null>>;
    floorFilter: FloorFilter;
    setFloorFilter: Dispatch<React.SetStateAction<FloorFilter>>;
    insulationFilter: InsulationFilter;
    setInsulationFilter: Dispatch<React.SetStateAction<InsulationFilter>>;
    shoppingListMobileVisible: boolean;
    setShoppingListMobileVisible: Dispatch<React.SetStateAction<boolean>>;
    locked: boolean;
    setLocked: Dispatch<React.SetStateAction<boolean>>;
}

export const ShoppingPlannerContext = createContext<ContextType>({
    roomLength: '',
    setRoomLength: () => {},
    roomWidth: '',
    setRoomWidth: () => {},
    roomSqm: '',
    setRoomSqm: () => {},
    wallLength: '',
    setWallLength: () => {},
    innerCorners: '',
    setInnerCorners: () => {},
    corners: '',
    setCorners: () => {},
    endCaps: '',
    setEndCaps: () => {},
    floors: [],
    selectedDecor: '',
    setSelectedDecor: () => {},
    accessories: {
        insulations: [],
        installationAndCare: [],
        skirtings: [],
    },
    selectedInsulation: '',
    setSelectedInsulation: () => {},
    selectedSkirtingDecor: '',
    setSelectedSkirtingDecor: () => {},
    selectedInstallationAndCare: [],
    setSelectedInstallationAndCare: () => {},
    sendUserInputToServer: () => {},
    slug: null,
    setSlug: () => {},
    floorFilter: {
        selectedFloorIds: [],
        textFilter: '',
    },
    setFloorFilter: () => {},
    insulationFilter: {
        activeSubsoil: null,
        activeRequirements: [],
    },
    setInsulationFilter: () => {},
    shoppingListMobileVisible: false,
    setShoppingListMobileVisible: () => {},
    locked: false,
    setLocked: () => {},
});

interface ShoppingPlannerContextValueProviderProps {}
export const ShoppingPlannerContextValueProvider: FunctionComponent<
    PropsWithChildren<ShoppingPlannerContextValueProviderProps>
> = (props) => {
    const sessionStorageData: StorageInterface | null = sessionStorage.getItem('logoclic-shopping-planner')
        ? JSON.parse(sessionStorage.getItem('logoclic-shopping-planner') || '')
        : null;

    // room page
    const [roomLength, setRoomLength] = useState<string>(sessionStorageData?.userInput.roomLength ?? '');
    const [roomWidth, setRoomWidth] = useState<string>(sessionStorageData?.userInput.roomWidth ?? '');
    const [roomSqm, setRoomSqm] = useState<string>(sessionStorageData?.userInput.roomSqm ?? '');

    //floor page
    const [floors, setFloors] = useState<Floors>([]);
    const [selectedFloor, setSelectedFloor] = useState(sessionStorageData?.userInput.selectedFloor ?? '');
    const [selectedDecor, setSelectedDecor] = useState(sessionStorageData?.userInput.selectedDecor ?? '');

    // skirting page
    const [wallLength, setWallLength] = useState<string>(sessionStorageData?.userInput.wallLength ?? '');
    const [innerCorners, setInnerCorners] = useState<string>(sessionStorageData?.userInput.innerCorners ?? '');
    const [corners, setCorners] = useState<string>(sessionStorageData?.userInput.corners ?? '');
    const [endCaps, setEndCaps] = useState<string>(sessionStorageData?.userInput.endCaps ?? '');

    //accessories page
    const [accessories, setAccessories] = useState<AccessoriesData>({
        installationAndCare: [],
        insulations: [],
        skirtings: [],
    });
    const [selectedInsulation, setSelectedInsulation] = useState(
        sessionStorageData?.userInput.selectedInsulation ?? ''
    );
    const [selectedSkirtingDecor, setSelectedSkirtingDecor] = useState(
        sessionStorageData?.userInput.selectedSkirtingDecor ?? ''
    );
    const [selectedInstallationAndCare, setSelectedInstallationAndCare] = useState(
        sessionStorageData?.userInput.selectedInstallationAndCare ?? []
    );

    const [slug, setSlug] = useState<string | null>(sessionStorageData?.slug ?? null);

    const locale = window.__GGLocale ?? '';

    // Filters
    const [floorFilter, setFloorFilter] = useState<FloorFilter>(
        sessionStorageData?.filter.floorFilter ?? { selectedFloorIds: [], textFilter: '' }
    );

    const [insulationFilter, setInsulationFilter] = useState<InsulationFilter>(
        sessionStorageData?.filter.insulationFilter ?? { activeSubsoil: null, activeRequirements: [] }
    );

    // Floor textfilter
    useEffect(() => {
        if (floorFilter.textFilter !== '') {
            floorTextFilter(setFloorFilter, floorFilter.textFilter, floors);
        }

        // preselect all floors if initial floor selection is empty
        if (floors.length > 0 && floorFilter.selectedFloorIds.length === 0 && floorFilter.textFilter === '') {
            setFloorFilter((currentFloorFilter) => ({
                ...currentFloorFilter,
                selectedFloorIds: floors.map((floor) => floor.floorFamilyId),
            }));
        }
    }, [floorFilter.textFilter, floors]);

    const [shoppingListMobileVisible, setShoppingListMobileVisible] = useState(false);

    const [locked, setLocked] = useState(false);

    useEffect(() => {
        // if we have a slug, always read values from server
        if (slug) {
            readAnswers(slug).then((response) => {
                setRoomLength(response.answers.userInput.roomLength);
                setRoomWidth(response.answers.userInput.roomWidth);
                setRoomSqm(response.answers.userInput.roomSqm);

                setWallLength(response.answers.userInput.wallLength);
                setInnerCorners(response.answers.userInput.innerCorners);
                setCorners(response.answers.userInput.corners);
                setEndCaps(response.answers.userInput.endCaps);

                setSelectedFloor(response.answers.userInput.selectedFloor);
                setSelectedDecor(response.answers.userInput.selectedDecor);

                setSelectedInsulation(response.answers.userInput.selectedInsulation);
                setSelectedSkirtingDecor(response.answers.userInput.selectedSkirtingDecor);
                setSelectedInstallationAndCare(response.answers.userInput.selectedInstallationAndCare);
                setFloorFilter(response.answers.filter.floorFilter);
                setInsulationFilter(response.answers.filter.insulationFilter);
            });
        }
    }, [slug]);

    const sendUserInputToServer = () => {
        persistAnswers(
            {
                userInput: {
                    roomLength,
                    roomWidth,
                    roomSqm,
                    wallLength,
                    innerCorners,
                    corners,
                    endCaps,
                    selectedFloor,
                    selectedDecor,
                    selectedInsulation,
                    selectedInstallationAndCare,
                    selectedSkirtingDecor,
                },
                filter: {
                    floorFilter,
                    insulationFilter,
                },
                slug,
            },
            locale
        ).then((response) => {
            setSlug(response.slug);
        });
    };

    useEffect(() => {
        if (slug) {
            sendUserInputToServer();
        }

        persistInSessionStorage({
            userInput: {
                roomLength,
                roomWidth,
                roomSqm,
                wallLength,
                innerCorners,
                corners,
                endCaps,
                selectedFloor,
                selectedDecor,
                selectedInsulation,
                selectedInstallationAndCare,
                selectedSkirtingDecor,
            },
            filter: {
                floorFilter,
                insulationFilter,
            },
            slug,
        });
    }, [
        roomLength,
        roomWidth,
        roomSqm,
        wallLength,
        innerCorners,
        corners,
        endCaps,
        selectedFloor,
        selectedDecor,
        selectedSkirtingDecor,
        selectedInstallationAndCare,
        selectedSkirtingDecor,
        selectedInsulation,
        floorFilter,
        insulationFilter,
    ]);

    // ToDo: Implement "global" error handling
    useEffect(() => {
        fetchFloors(locale).then((floors) => setFloors(floors));
        fetchAccessories(locale).then((accessories) => setAccessories(accessories));
    }, []);

    const setSelectedDecorAndUpdateInsulation = (articleNumber: string): void => {
        // Since we do not display a list of insulations when the floor does not need insulation,
        // we must unset the selected insulation here because the user cannot unselect the insulation.
        setSelectedDecor(articleNumber);
        const floor = floors.find((floor) => floor.decors.some((decor) => decor.articleNumber === articleNumber));
        if (floor?.doesNotNeedInsulation) {
            setSelectedInsulation('');
        }
    };

    return (
        <ShoppingPlannerContext.Provider
            value={{
                roomLength,
                setRoomLength,
                roomWidth,
                setRoomWidth,
                roomSqm,
                wallLength,
                setWallLength,
                innerCorners,
                setInnerCorners,
                corners,
                setCorners,
                endCaps,
                setEndCaps,
                setRoomSqm,
                floors,
                selectedDecor,
                setSelectedDecor: setSelectedDecorAndUpdateInsulation,
                accessories,
                selectedInsulation,
                setSelectedInsulation,
                selectedSkirtingDecor,
                setSelectedSkirtingDecor,
                selectedInstallationAndCare,
                setSelectedInstallationAndCare,
                sendUserInputToServer,
                slug,
                setSlug,
                floorFilter,
                setFloorFilter,
                insulationFilter,
                setInsulationFilter,
                shoppingListMobileVisible,
                setShoppingListMobileVisible,
                locked,
                setLocked,
            }}
        >
            {props.children}
        </ShoppingPlannerContext.Provider>
    );
};

export interface StorageInterface {
    userInput: {
        roomLength: string;
        roomWidth: string;
        roomSqm: string;
        wallLength: string;
        innerCorners: string;
        corners: string;
        endCaps: string;
        selectedFloor: string;
        selectedDecor: string;
        selectedInsulation: string;
        selectedInstallationAndCare: string[];
        selectedSkirtingDecor: string;
    };
    filter: {
        floorFilter: FloorFilter;
        insulationFilter: InsulationFilter;
    };
    slug: string | null;
}

const persistInSessionStorage = (storageItem: StorageInterface) => {
    sessionStorage.setItem('logoclic-shopping-planner', JSON.stringify(storageItem));
};

const floorTextFilter = (
    setFloorFilter: Dispatch<React.SetStateAction<FloorFilter>>,
    textFilter: string,
    floors: Floors
) => {
    let filteredFloors = floors.filter(
        (floor) => floor.floorFamilyLabel.toLocaleLowerCase().indexOf(textFilter.toLocaleLowerCase()) !== -1
    );

    // no filter result for floors, let's filter decors
    if (filteredFloors.length === 0) {
        floors.forEach((floor) => {
            const filteredDecors = floor.decors.filter(
                (decor) =>
                    decor.articleNumber.toLocaleLowerCase().indexOf(textFilter.toLocaleLowerCase()) !== -1 ||
                    decor.label.toLocaleLowerCase().indexOf(textFilter.toLocaleLowerCase()) !== -1
            );

            // if we have a hit for decors, add the floor to the filtered Floors result but only including the filtered decors
            if (filteredDecors.length > 0) {
                const floorWithFilteredDecors = { ...floor, decors: filteredDecors };
                filteredFloors = [...filteredFloors, floorWithFilteredDecors];
            }
        });
    }

    setFloorFilter((currentFloorFilter) => ({
        ...currentFloorFilter,
        selectedFloorIds: filteredFloors.map((filteredFloor) => filteredFloor.floorFamilyId),
    }));
};
